import { Link } from '@tanstack/react-router';

import { Eyebrow, Tag } from '@/components/editorial';
import { AppImage } from '@/components/layout/app-image';
import type { PublicProjectRecord } from '@/content/schemas';

import { ContactCta } from './featured-project-card';

type ProjectDetailPageProps = {
  project: PublicProjectRecord;
  bodyHtml: string;
};

function formatDate(publishedAt: string) {
  return new Date(publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
}

export function ProjectDetailPage({
  project,
  bodyHtml,
}: ProjectDetailPageProps) {
  return (
    <article className="w-full">
      <div className="border-border w-full border-b">
        <div className="container max-w-3xl px-6 py-16 md:py-24">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link
              to="/projects"
              className="text-muted-foreground hover:text-brand font-mono text-xs transition-colors"
            >
              ← Projects
            </Link>
            <span className="text-border">·</span>
            <Tag variant="outline">Case study</Tag>
          </div>

          <Eyebrow className="mb-4">{formatDate(project.publishedAt)}</Eyebrow>

          <h1 className="font-display text-ink mb-6 text-4xl font-semibold leading-tight md:text-5xl">
            {project.title}
          </h1>

          <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
            {project.description}
          </p>

          {project.outcomes && project.outcomes.length > 0 && (
            <dl className="border-border mb-8 grid grid-cols-2 gap-4 border-y py-6 sm:grid-cols-4">
              {project.outcomes.map((outcome) => (
                <div key={`${outcome.label}-${outcome.value}`}>
                  <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
                    {outcome.label}
                  </dt>
                  <dd className="font-display text-ink text-base font-semibold">
                    {outcome.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {project.facts && project.facts.length > 0 && (
            <ul className="text-muted-foreground mb-8 space-y-2 text-sm leading-relaxed">
              {project.facts.map((fact) => (
                <li key={fact} className="flex gap-2.5">
                  <span
                    aria-hidden
                    className="bg-brand/70 mt-2 size-1.5 shrink-0 rounded-full"
                  />
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="text-muted-foreground flex flex-wrap items-center gap-4 font-mono text-xs">
            {project.role && <span>{project.role}</span>}
            {(project.links?.live || project.links?.github) && (
              <>
                <span>·</span>
                <div className="flex flex-wrap gap-3">
                  {project.links?.live && (
                    <a
                      href={project.links.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:text-brand-soft transition-colors"
                    >
                      Live site ↗
                    </a>
                  )}
                  {project.links?.github && (
                    <a
                      href={project.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:text-brand-soft transition-colors"
                    >
                      GitHub ↗
                    </a>
                  )}
                </div>
              </>
            )}
          </div>

          {project.services.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-1.5">
              {project.services.map((service) => (
                <Tag key={service} variant="muted">
                  {service}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <div className="container max-w-4xl px-6">
          <div className="relative aspect-video w-full overflow-hidden rounded-b-xl">
            <AppImage
              src={project.hero.src}
              alt={project.hero.alt}
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>

      <div className="container max-w-3xl px-6 py-12 md:py-16">
        <div
          className="prose-custom min-w-0"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        <div className="border-border bg-muted/30 mt-12 rounded-2xl border p-6 md:p-8">
          <h2 className="font-display text-ink text-2xl font-semibold">
            Interested in similar outcomes?
          </h2>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            I&apos;m targeting Singapore remote full time and selective contract
            work with clear product impact.
          </p>
          <div className="mt-5">
            <ContactCta label="Start a conversation" />
          </div>
        </div>

        <div className="mt-12">
          <Link
            to="/projects"
            className="text-muted-foreground hover:text-brand font-mono text-sm transition-colors"
          >
            ← All projects
          </Link>
        </div>
      </div>
    </article>
  );
}
